import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../Components/auth/firebaseConfig';
import sendEmail from './SendEmail';
import { fetchUserEmailById } from '../DatabaseApis/DBQuery';

// Function to check if a date is within specified days
const isWithinDays = (dueDate, days) => {
  const now = new Date();
  const due = new Date(dueDate);
  const diffTime = due - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= days;
};

// Function to send due date notification email
const sendDueDateNotification = async (card, assigneeEmail, days) => {
  const emailDetails = {
    cardName: card.taskName,
    taskId: card.cardId,
    description: card.description || "N/A",
    assigneeName: card.assigneeName || "N/A",
    priority: card.priority || "N/A",
    status: card.cardStatus || "N/A",
    dueDate: card.dueDate ? new Date(card.dueDate).toLocaleDateString() : "N/A",
    estimate: card.estimate ? `${card.estimate} hrs` : "N/A",
    acceptanceCriteria: card.acceptanceCriteria || "N/A",
    assignDate: card.assignDate ? new Date(card.assignDate).toLocaleDateString() : "N/A",
    subtasks: card.subtasks?.length > 0 ? card.subtasks : "N/A",
    webAppLink: "https://ovplan1.web.app",
  };

  await sendEmail(
    assigneeEmail,
    `Due Date Reminder: ${card.taskName} is due in ${days} days`,
    emailDetails
  );
};

// Helper to fetch the transition matrix for a project
const fetchTransitionMatrix = async (projectId) => {
  if (!projectId) return null;
  const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
  const matrixDoc = await getDoc(matrixRef);
  return matrixDoc.exists() ? matrixDoc.data() : null;
};

// Helper to get the next allowed state from the matrix
const getNextAllowedState = (currentStatus, transitionMatrix) => {
  if (!transitionMatrix || !transitionMatrix[currentStatus]) return null;
  const nextStates = Object.entries(transitionMatrix[currentStatus])
    .filter(([to, allowed]) => allowed)
    .map(([to]) => to)
    .filter(to => to !== currentStatus); // Exclude self-transition
  return nextStates.length > 0 ? nextStates[0] : null;
};

// Main function to check due dates and send notifications
export const checkDueDatesAndNotify = async () => {
  try {
    // Get all cards and rules
    const [cardsSnapshot, rulesSnapshot] = await Promise.all([
      getDocs(collection(db, 'cards')),
      getDocs(collection(db, 'rules'))
    ]);
    
    const rules = rulesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter active rules with email notifications
    const emailRules = rules.filter(rule => 
      rule.isActive && 
      rule.blocks?.some(block => 
        block.actions?.some(action => action.value === 'send_email') &&
        block.conditions?.some(cond => 
          cond.field === 'dueDate' && 
          cond.operator === 'within_days'
        )
      )
    );

    // Filter active rules with move_card action
    const moveCardRules = rules.filter(rule =>
      rule.isActive &&
      rule.blocks?.some(block =>
        block.actions?.some(action => action.value === 'move_card')
      )
    );

    for (const docSnap of cardsSnapshot.docs) {
      const card = docSnap.data();
      const cardId = docSnap.id;
      // Skip if no due date or card is done
      if (!card.dueDate || card.cardStatus === 'Done') continue;

      // --- EMAIL NOTIFICATION LOGIC (existing) ---
      for (const rule of emailRules) {
        for (const block of rule.blocks) {
          const dueDateCondition = block.conditions.find(
            cond => cond.field === 'dueDate' && cond.operator === 'within_days'
          );
          if (dueDateCondition && isWithinDays(card.dueDate, parseInt(dueDateCondition.value))) {
            const assigneeEmail = await fetchUserEmailById(card.assigneeId);
            if (assigneeEmail) {
              await sendDueDateNotification(card, assigneeEmail, dueDateCondition.value);
            }
          }
        }
      }

      // --- MOVE CARD WORKFLOW ---
      for (const rule of moveCardRules) {
        for (const block of rule.blocks) {
          // If the card matches the block's conditions
          const matches = (block.conditions || []).every(cond => {
            let cardField = cond.field;
            if (cardField === 'status') cardField = 'cardStatus';
            if (cardField === 'assignee') cardField = 'assigneeName';
            if (cardField === 'assigneeEmail') cardField = 'assigneeEmail';
            const cardValue = card[cardField];
            switch (cond.operator) {
              case 'equals':
                if (cardField === 'dueDate') {
                  const cardDate = new Date(cardValue);
                  const compareDate = new Date(cond.value);
                  return cardDate.toDateString() === compareDate.toDateString();
                }
                if (cardField === 'assigneeName' || cardField === 'assigneeEmail') {
                  return cardValue?.toLowerCase() === cond.value?.toLowerCase();
                }
                return cardValue === cond.value;
              case 'not_equals':
                if (cardField === 'dueDate') {
                  const cardDate = new Date(cardValue);
                  const compareDate = new Date(cond.value);
                  return cardDate.toDateString() !== compareDate.toDateString();
                }
                if (cardField === 'assigneeName' || cardField === 'assigneeEmail') {
                  return cardValue?.toLowerCase() !== cond.value?.toLowerCase();
                }
                return cardValue !== cond.value;
              case 'greater_than':
                if (cardField === 'dueDate') {
                  const cardDate = new Date(cardValue);
                  const compareDate = new Date(cond.value);
                  return cardDate > compareDate;
                }
                return cardValue > cond.value;
              case 'less_than':
                if (cardField === 'dueDate') {
                  const cardDate = new Date(cardValue);
                  const compareDate = new Date(cond.value);
                  return cardDate < compareDate;
                }
                return cardValue < cond.value;
              case 'within_days':
                if (cardField === 'dueDate' && cardValue) {
                  const now = new Date();
                  const dueDate = new Date(cardValue);
                  const diffTime = dueDate - now;
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  const days = parseInt(cond.value);
                  return diffDays >= 0 && diffDays <= days;
                }
                return false;
              default:
                return false;
            }
          });
          if (matches) {
            // Fetch the transition matrix for the card's project
            const projectId = card.projectId;
            const transitionMatrix = await fetchTransitionMatrix(projectId);
            const nextState = getNextAllowedState(card.cardStatus, transitionMatrix);
            if (nextState && card.cardStatus !== nextState) {
              await updateDoc(doc(db, 'cards', cardId), { cardStatus: nextState });
              // Optionally, log or notify about the move
              console.log(`Moved card ${card.cardId} to ${nextState} (project: ${projectId})`);
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('Error checking due dates and moving cards:', error);
  }
};

// Function to start the scheduled task
export const startDueDateCheck = () => {
  // Check immediately when started
  checkDueDatesAndNotify();
  
  // Then check every hour
  setInterval(checkDueDatesAndNotify, 60 * 60 * 1000);
}; 